import { TournamentState } from '../../types/tournament';
import { ClassOption, IScheduleConstraints, ScheduleMode } from '../../types/schedule';
import { ClassMatchType } from '../../types/enums';
import { fromZonedTime } from 'date-fns-tz';
import {
  addCalendarDays,
  diffCalendarDaysInclusive,
  getVenueLocalParts,
} from '../../utils/tournamentDateTime';

export const generateDefaultSchedulingConstraints = (
  tournamentInfo: TournamentState,
  timeZone: string
): IScheduleConstraints => {
  const startParts = getVenueLocalParts(tournamentInfo.startDate!, timeZone);
  const endParts = getVenueLocalParts(tournamentInfo.endDate!, timeZone);

  const numberOfDays = Math.max(1, diffCalendarDaysInclusive(startParts.date, endParts.date));

  const scheduleConstraints: IScheduleConstraints = {
    allow_split: true,
    allow_alignment: false,
    pull_to_lower_court: false,
    gap_group_group: 0,
    max_gap_group_group: 20,
    gap_group_ko: 0,
    max_gap_group_ko: 40,
    gap_ko_ko: 0,
    max_gap_ko_ko: 20,
    s1_time_limit: 10,
    s2_time_limit: 5,
    stream_courts: [],
    max_chunk_size: 2,
    number_of_chunk_splits: 1,
    classOverrides: {},
    scheduleTimezone: timeZone,
    tournamentStartTime: tournamentInfo.startDate!,
    tournamentEndTime: tournamentInfo.endDate!,
    scheduleMode: ScheduleMode.SEQUENTIAL,
    days: new Array(numberOfDays).fill(null).map((_, index) => {
      const currentDayStr = addCalendarDays(startParts.date, index);

      const sessionStartTime = index === 0 ? startParts.time : '09:00';
      const localStartStr = `${currentDayStr}T${sessionStartTime}:00`;
      const localEndStr = `${currentDayStr}T${endParts.time}:00`;

      const defaultMatchLength = 20;
      const defaultPauseLength = 5;

      const matchLengths = Object.keys(ClassMatchType)
        .filter((k) => !Number.isNaN(+k))
        .reduce((accumulator, value) => {
          const coerced: number = +value;
          accumulator[coerced as keyof typeof accumulator] = defaultMatchLength;
          return accumulator;
        }, {} as { [matchType in ClassMatchType]: number });

      const classOptions: ClassOption[] = tournamentInfo.classes.reduce((opts, clazz) => {
        const classOption: ClassOption = {
          class: clazz,
          courts: tournamentInfo.courts,
          matchLengths: matchLengths,
        };
        return [...opts, classOption];
      }, [] as ClassOption[]);

      return {
        dayKey: index,
        startTime: fromZonedTime(localStartStr, timeZone).toISOString(),
        endTime: fromZonedTime(localEndStr, timeZone).toISOString(),
        defaultMatchLength: defaultMatchLength,
        defaultPauseLength: defaultPauseLength,
        classes: tournamentInfo.classes,
        classOptions: classOptions,
        courts: tournamentInfo.courts,
      };
    }),
    tournamentClasses: tournamentInfo.classes,
    tournamentCourts: tournamentInfo.courts,
  };

  return scheduleConstraints;
};
